// Delivery flow orchestrator (User Portal): portal-scoped auth/robot APIs
// ('portalToken', /api/portal/robots) and a portal robot selector in SelectionPhase.
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import { io } from "socket.io-client";
import axios from "axios";
import CONFIG from "../../config/config";
import { apiFetch, withAuthErrorHandling } from "../../services/http";
import { MAX_STOPS, STATUS_MESSAGES, getStopLabel } from "./deliveryConstants";
import SelectionPhase from "./phases/SelectionPhase";
import NavigatingPhase from "./phases/NavigatingPhase";
import StoppedPhase from "./phases/StoppedPhase";
import CancelledPhase from "./phases/CancelledPhase";
import CompletePhase from "./phases/CompletePhase";
import { submitReview } from "../../services/reviews.api";
import { useRobotCharging } from "../../hooks/useRobotCharging";
import { useBoxedIn } from "../../hooks/useBoxedIn";
import { isBoxedInFailure } from "../../utils/boxedIn";

const MISSION_SESSION_KEY = "botriq:missionState";

// nav_progress states that end a leg without arriving. "boxed_in"/"stuck" are
// deliberately absent: the robot is holding mid-trip and carries on by itself.
const NAV_FAILURE_STATES = new Set(["failed", "aborted", "error"]);

// A robot's assignedMap arrives populated ({_id,name}) from v1 and as a bare
// id string from legacy records.
const readMapId = (assignedMap) =>
  (typeof assignedMap === "object" ? assignedMap?._id : assignedMap) || null;

// Nav progress arrives as either a 0–1 fraction or an already-scaled 0–100
// number depending on the source event; normalize to a 0–100 percent.
const normalizeProgressPct = (rawProgress) => {
  const raw = typeof rawProgress === "number" ? rawProgress : 0;
  return raw > 1 ? Math.round(raw) : Math.round(raw * 100);
};

// Distance-based progress estimate, steadier than the robot's own `progress`.
// Baselined once per leg and floored at the highest value already shown.
const withDistanceProgress = (navData, progressPct, navStartDistRef, navProgressFloorRef) => {
  const hasLivePosition =
    navData.state === "moving" &&
    typeof navData.x === "number" &&
    typeof navData.y === "number" &&
    typeof navData.target_x === "number" &&
    typeof navData.target_y === "number";

  let candidate = progressPct;

  if (hasLivePosition) {
    const remainDist = Math.hypot(navData.target_x - navData.x, navData.target_y - navData.y);
    // Capture the journey length ONCE per leg. Deliberately not re-raised
    // later — see (1) above.
    if (navStartDistRef.current === null) {
      navStartDistRef.current = remainDist;
    }
    const totalDist = navStartDistRef.current;
    if (totalDist > 0.01) {
      // Clamped at 0: the robot moving further out than it started reads as
      // "no progress yet", never as a negative that would fight the floor.
      const distPct = Math.max(0, Math.round((1 - remainDist / totalDist) * 100));
      candidate = distPct;
    }
  }

  // The floor: progress toward a destination never decreases on screen.
  navProgressFloorRef.current = Math.max(navProgressFloorRef.current, candidate);
  return navProgressFloorRef.current;
};

// Plain-language status line shown while navigating, keyed off progress.
const statusForProgress = (progressPct) => {
  if (progressPct >= 80) return "Final approach — almost there!";
  if (progressPct >= 60) return "Locating delivery point";
  if (progressPct >= 40) return "Slowing down";
  return "Approaching destination";
};

/** Turn a rejected navigate call into something the user can act on. */
const navigateFailureMessage = ({ status, body }) => {
  const reason = body?.reason ?? body?.queue?.reason;

  if (reason === "ALREADY_NAVIGATING" || status === 409) {
    const target = body?.activeTarget ?? body?.queue?.active_target;
    return target
      ? `This robot is already navigating to ${target}. Wait for it to finish, or cancel that trip first.`
      : "This robot is already in navigation. Wait for it to finish, or cancel the current trip first.";
  }

  if (reason === "ROBOT_UNREACHABLE") {
    return "The robot is not reachable right now. Wait for it to reconnect and try again.";
  }

  return "Could not start navigation — the robot did not accept the command. Please check the robot is connected and try again.";
};

// Stops are rows the user reorders, clears and removes, and two rows can hold
// the same location name — neither the index nor the name is a stable React
// key, so each row carries an id minted when the row is created.
let stopIdSeq = 0;
const makeStop = (name = "") => {
  stopIdSeq += 1;
  return { id: `stop-${stopIdSeq}`, name };
};
const blankStops = () => [makeStop()];

const RoomNumberPageAero = () => {
  const location = useLocation();
  // Preset robot id from nav state (Robot Details ▸ Operations). Read once —
  // after that, this page's own robot switching owns the selection.
  const presetRobotId = location.state?.presetRobotId ?? null;

  // ── State ──────────────────────────────────────────────────────────────
  const [selectedMap, setSelectedMap] = useState(null);
  const [mapsAndIds, setMapsAndIds] = useState([]);
  const [mapLocations, setMapLocations] = useState([]);
  const [stops, setStops] = useState(blankStops);
  const [phase, setPhase] = useState("selection");
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [destinations, setDestinations] = useState([]);
  const [navProgress, setNavProgress] = useState(0);
  const [robotState, setRobotState] = useState("IDLE");
  const [isRobotConnected, setIsRobotConnected] = useState(false);
  // Mirror of isRobotConnected for callbacks, which would otherwise capture
  // whatever the value was when they were created.
  const isRobotConnectedRef = useRef(false);
  // sendNavigateCommand is declared below the socket handlers that need it; a
  // ref keeps that one call site from forcing a reorder of the component.
  const sendNavigateCommandRef = useRef(null);
  const [currentStatus, setCurrentStatus] = useState(STATUS_MESSAGES[0]);
  const [stoppedProgress, setStoppedProgress] = useState(0);
  // The leg ended because the robot gave up boxed in: the stopped screen then
  // offers "Try again" (re-send the goal) instead of Resume.
  const [boxedInFailure, setBoxedInFailure] = useState(false);
  const [cancelReturnPoint, setCancelReturnPoint] = useState(null);
  const [homeLocation, setHomeLocation] = useState(null);
  const [isDoorOpen, setIsDoorOpen] = useState(false);
  const [showMapSelectionModalDialog, setShowMapSelectionModalDialog] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [navigationTarget, setNavigationTarget] = useState(null);
  const [robotSocketId, setRobotSocketId] = useState(null);
  const [serialID, setSerialID] = useState(null);
  const [batteryLevel, setBatteryLevel] = useState(null);
  // MANUAL = user clicks Next Stop; AUTO = auto-advance after a countdown. No
  // UI sets this yet — named so a future picker is a one-line change.
  const navMode = "MANUAL";

  // Portal-specific: list of robots assigned to this portal user
  const [portalRobots, setPortalRobots] = useState([]);

  const socketRef = useRef(null);
  const phaseRef = useRef("selection");
  const hasCompletedRef = useRef(false);
  // Mirrors `destinations`/`currentStopIndex` for the socket handler below, whose
  // effect only re-subscribes on `robotId` change and would otherwise close over
  // stale (possibly already-cleared) state.
  const destinationsRef = useRef(destinations);
  const currentStopIndexRef = useRef(currentStopIndex);
  const navStartDistRef = useRef(null); // journey distance captured once per leg — denominator for progress %
  // Highest progress % already shown for the current leg. The bar is clamped to
  // this so it can never run backwards — see withDistanceProgress for why it did.
  const navProgressFloorRef = useRef(0);
  const emergencyStopActive = useRef(false);

  /** Start a new progress leg. `floor` is non-zero only for Resume. */
  const resetNavProgressLeg = (floor = 0) => {
    navStartDistRef.current = null;
    navProgressFloorRef.current = floor;
  };

  useEffect(() => {
    destinationsRef.current = destinations;
  }, [destinations]);
  useEffect(() => {
    currentStopIndexRef.current = currentStopIndex;
  }, [currentStopIndex]);

  // ── Robot identity ─────────────────────────────────────────────────────
  const getStoredRobot = () => {
    try {
      return JSON.parse(localStorage.getItem("selectedRobo") || "null");
    } catch {
      return null;
    }
  };
  const [robot, setRobot] = useState(getStoredRobot);
  const robotId = robot?.serialID;

  // Charging flag comes from the shared hook so this page, patrol and the
  // admin portal all read the same SENSOR_STATUS_UPDATE-backed source. Seeded
  // from the robot record so a docked robot blocks dispatch on first paint.
  const batteryCharging = useRobotCharging(robotId, robot);
  // Boxed in (front and both sides blocked): banner on the trip, and the
  // give-up shown as "needs manual help" instead of a generic pause.
  const { view: boxedIn } = useBoxedIn(robotId);

  /** Uses /api/portal/robots (portal token), not /api/robots (admin token). */
  const syncRobotFromPortalList = useCallback(async (storedRobot) => {
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.error("[DeliveryPage] Failed to fetch portal robots, status:", res.status);
        return;
      }
      const list = await res.json();
      // list may be { robots: [...] } or just [...]
      const robots = Array.isArray(list) ? list : list.robots || list.data || [];
      setPortalRobots(robots);

      if (storedRobot) {
        const match = robots.find(
          (r) => r._id === storedRobot._id || r.serialID === storedRobot.serialID
        );
        if (match) {
          const raw = match.roverType;
          const normalizedType = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
          // `match` first fills in a bare `{ _id }` preset from the fetched
          // record; `storedRobot`'s own fields still win on top.
          const merged = {
            ...match,
            ...storedRobot,
            roverType: normalizedType,
            assignedMap: match.assignedMap ?? storedRobot.assignedMap ?? null,
          };
          setRobot(merged);
          localStorage.setItem("selectedRobo", JSON.stringify(merged));
        }
      } else if (robots.length === 1) {
        // Auto-select the only robot if none stored
        const r = robots[0];
        const raw = r.roverType;
        const normalizedType = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
        const auto = { ...r, roverType: normalizedType };
        setRobot(auto);
        localStorage.setItem("selectedRobo", JSON.stringify(auto));
      }
    } catch (err) {
      console.error("[DeliveryPage] syncRobot error:", err);
    }
  }, []);

  // Restore active mission state when navigating back to this page
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(MISSION_SESSION_KEY);
      if (!saved) return;
      const s = JSON.parse(saved);
      if (!s.phase || s.phase === "selection") return;
      // Only restore if same robot
      const storedRobot = getStoredRobot();
      if (s.robotId && storedRobot?.serialID && s.robotId !== storedRobot.serialID) return;
      phaseRef.current = s.phase;
      setPhase(s.phase);
      // Sessions written before stops carried ids restore without one; mint
      // them here so the journey strip always has a stable key.
      if (s.destinations)
        setDestinations(s.destinations.map((d) => (d.id ? d : { ...d, id: makeStop().id })));
      if (s.currentStopIndex) setCurrentStopIndex(s.currentStopIndex);
      if (s.navProgress) {
        setNavProgress(s.navProgress);
        // Seed the floor to match, or the first live tick after remount can
        // snap the bar below the restored value.
        navProgressFloorRef.current = s.navProgress;
      }
      if (s.homeLocation) setHomeLocation(s.homeLocation);
      if (s.robotState) setRobotState(s.robotState);
      if (s.currentStatus) setCurrentStatus(s.currentStatus);
      if (s.stoppedProgress) setStoppedProgress(s.stoppedProgress);
      if (s.cancelReturnPoint) setCancelReturnPoint(s.cancelReturnPoint);
      if (s.selectedMap) setSelectedMap(s.selectedMap);
      const dest = s.destinations?.[s.currentStopIndex ?? 0];
      if (dest) setNavigationTarget(dest);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist active mission state to sessionStorage so it survives tab/page switches
  useEffect(() => {
    if (phase === "selection") {
      sessionStorage.removeItem(MISSION_SESSION_KEY);
      return;
    }
    try {
      sessionStorage.setItem(
        MISSION_SESSION_KEY,
        JSON.stringify({
          phase,
          destinations,
          currentStopIndex,
          navProgress,
          homeLocation,
          robotState,
          currentStatus,
          stoppedProgress,
          cancelReturnPoint,
          selectedMap,
          robotId,
        })
      );
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    destinations,
    currentStopIndex,
    navProgress,
    homeLocation,
    robotState,
    currentStatus,
    stoppedProgress,
    cancelReturnPoint,
    selectedMap,
  ]);

  // On mount: load portal robots + listen for cross-tab robot changes. A
  // preset robot id wins over whatever was last stored.
  useEffect(() => {
    syncRobotFromPortalList(presetRobotId ? { _id: presetRobotId } : getStoredRobot());
    const handleStorageChange = (e) => {
      if (e.key === "selectedRobo") {
        try {
          const updated = JSON.parse(e.newValue || "null");
          if (updated) syncRobotFromPortalList(updated);
          else setRobot(null);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Persist the guest's rating. No deliveryId exists here (this app drives the
   *  robot directly), so the backend resolves the run by serialID instead. */
  const handleRate = useCallback(
    (value, comment) => {
      // `type` is explicit even though 'delivery' is the default: it is what
      // keeps the backend resolving a Delivery row for this review, and the
      // patrol flow now passes 'patrol' to suppress exactly that.
      submitReview({
        serialID: robotId || serialID,
        rating: value,
        comment,
        type: "delivery",
      }).catch((err) => console.error("Failed to submit review:", err));
    },
    [robotId, serialID]
  );

  // Resolve the new robot's map directly: the assignedMap effect below is keyed
  // off the memoized `assignedMap` object, so it won't re-fire if the new robot
  // happens to share the same assigned map as the previous one.
  const handleRobotChange = (robotId) => {
    const selected = portalRobots.find((r) => r._id === robotId);
    if (!selected) return;
    const raw = selected.roverType;
    const normalizedType = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
    const r = { ...selected, roverType: normalizedType };
    setRobot(r);
    localStorage.setItem("selectedRobo", JSON.stringify(r));
    // Reset stops when switching robot
    setMapLocations([]);
    setStops(blankStops());
    setIsRobotConnected(false);
    setRobotSocketId(null);
    const newAssignedMapId = readMapId(r.assignedMap);
    const newAssignedMap = newAssignedMapId
      ? mapsAndIds.find((m) => m._id === newAssignedMapId) || null
      : null;
    setSelectedMap(newAssignedMap);
  };

  // Reset the flow when the robot changes, so the previous robot's
  // phase/progress/destinations aren't shown against the new one.
  const prevRobotIdRef = useRef(robotId);
  useEffect(() => {
    if (prevRobotIdRef.current === robotId) return;
    prevRobotIdRef.current = robotId;
    setPhase("selection");
    phaseRef.current = "selection";
    setDestinations([]);
    setCurrentStopIndex(0);
    setNavProgress(0);
    resetNavProgressLeg();
    setNavigationTarget(null);
    setIsDoorOpen(false);
    setHomeLocation(null);
    hasCompletedRef.current = false;
  }, [robotId]);

  // Keep phaseRef in sync
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // ── Text-to-speech helper ──────────────────────────────────────────────
  const handleSpeak = (msg) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(msg);
      utterance.volume = 0.5;
      window.speechSynthesis.speak(utterance);
    }
  };

  // ── Data fetching ──────────────────────────────────────────────────────
  // The maps this portal user may see (venue + individual grants), so an admin
  // access change only needs a re-call — see USER_ACCESS_UPDATED below.
  const fetchMapsandId = useCallback(async () => {
    try {
      const token = localStorage.getItem("portalToken");
      const res = await withAuthErrorHandling(() =>
        axios.get(`${CONFIG.BASE_URL}/api/maps/map-ids`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );
      setMapsAndIds(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching accessible maps:", error);
    }
  }, []);

  useEffect(() => {
    fetchMapsandId();
  }, [fetchMapsandId]);

  // The robot's assigned map drives the flow, matched against the fetched list
  // — a deleted map leaves a dangling id, so an unmatched id means "no map".
  const assignedMapId = readMapId(robot?.assignedMap);
  const assignedMap = useMemo(
    () => (assignedMapId ? mapsAndIds.find((m) => m._id === assignedMapId) || null : null),
    [assignedMapId, mapsAndIds]
  );
  // True only once we know the robot has no usable map — nothing assigned, or
  // the assigned map is gone. Gated on the list having loaded so it can't
  // flash during the fetch.
  const needsManualMap = !!robot && mapsAndIds.length > 0 && !assignedMap;

  // Adopt the assigned map automatically — also when the assignment changes
  // while the same robot stays selected, not just on switch or initial load.
  useEffect(() => {
    if (!assignedMap) return;
    setSelectedMap((prev) => (prev?._id === assignedMap._id ? prev : assignedMap));
  }, [assignedMap]);

  useEffect(() => {
    const fetchMapDetails = async () => {
      if (!selectedMap?._id) {
        setMapLocations([]);
        setStops(blankStops());
        return;
      }
      try {
        const token = localStorage.getItem("portalToken");
        const res = await withAuthErrorHandling(() =>
          axios.get(`${CONFIG.BASE_URL}/api/maps/${selectedMap._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
        const rawData = res?.data?.mapCoordinate;
        if (!rawData) {
          setMapLocations([]);
          return;
        }
        const cleanedLocations = rawData.map((item) => {
          const name = Object.keys(item)[0];
          const values = item[name];
          return { name, coords: { x: values[0], y: values[1], yaw: values[2] } };
        });
        setMapLocations(cleanedLocations);
        setStops(blankStops());
      } catch (error) {
        console.error("Error fetching map details:", error);
        setMapLocations([]);
      }
    };
    fetchMapDetails();
  }, [selectedMap]);

  useEffect(() => {
    const checkoutMapForRobot = async () => {
      if (!selectedMap?._id || !robotId) return;
      try {
        const token = localStorage.getItem("portalToken");
        const response = await withAuthErrorHandling(() =>
          axios.get(`${CONFIG.BASE_URL}/api/maps/checkout-map`, {
            params: { mapId: selectedMap._id, serialID: robotId },
            headers: { Authorization: `Bearer ${token}` },
          })
        );
        if (response.data?.data?.socketId)
          setRobotSocketId({ socketID: response.data.data.socketId });
      } catch (error) {
        console.error("Error calling checkout-map API:", error);
      }
    };
    checkoutMapForRobot();
  }, [selectedMap, robotId]);

  // Re-subscribe to robot data when the user returns to this browser tab
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const s = socketRef.current;
      if (!s?.connected || !robotId) return;
      s.emit("subscribe-robot", robotId);
      s.emit("frontend:get-BATTERY_STATUS", { serialID: robotId });
      s.emit("frontend:get-SENSOR_STATUS", { serialID: robotId });
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [robotId]);

  // ── Socket.io subscription ─────────────────────────────────────────────
  useEffect(() => {
    const socket = io(CONFIG.BASE_URL);
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("frontend:get-device", { robotId });
      if (robotId) {
        socket.emit("subscribe-robot", robotId);
        socket.emit("frontend:get-BATTERY_STATUS", { serialID: robotId });
        socket.emit("frontend:get-SENSOR_STATUS", { serialID: robotId });
      }
      // Join this portal user's room so admin-side access changes reach us.
      try {
        const pu = JSON.parse(localStorage.getItem("portalUser") || "null");
        const uid = pu?.id || pu?._id;
        if (uid) socket.emit("JOIN_PORTAL_USER_ROOM", String(uid));
      } catch {
        /* no-op */
      }
    });

    // Admin changed this user's map/venue access → refetch the accessible maps
    // so the selector reflects it live (no re-login needed).
    socket.on("USER_ACCESS_UPDATED", () => {
      fetchMapsandId();
    });

    socket.on("get_device_data", (data) => {
      if (data?.serialID && robotId && data.serialID !== robotId) return;
      if (data.pythonSocketId) {
        setRobotSocketId(data.pythonSocketId);
        setSerialID(data.serialID);
        setIsRobotConnected(true);
      } else {
        setRobotSocketId(null);
        setSerialID(null);
        setIsRobotConnected(false);
      }
    });

    socket.on("robot_connected", (data) => {
      if (data?.serialID && robotId && data.serialID !== robotId) return;
      if (data.pythonSocketId) {
        setRobotSocketId(data.pythonSocketId);
        setSerialID(data.serialID);
      }
      setIsRobotConnected(true);
    });

    socket.on("robot_disconnected", (data) => {
      if (data?.serialID && robotId && data.serialID !== robotId) return;
      setIsRobotConnected(false);
      setRobotSocketId(null);
    });

    const handleNavData = (navData) => {
      if (!navData) return;

      if (hasCompletedRef.current) return;

      if (phaseRef.current === "returningToBase") {
        if (navData.state === "reached") {
          setNavProgress(100);
          setRobotState("REACHED");
          setTimeout(() => {
            phaseRef.current = "selection";
            setPhase("selection");
            setStops(blankStops());
            setDestinations([]);
            setCurrentStopIndex(0);
          }, 1500);
        } else {
          // Routed through withDistanceProgress so a stale telemetry frame on
          // the return leg can't regress the bar.
          const reported = normalizeProgressPct(navData.progress);
          setNavProgress(
            Math.min(
              withDistanceProgress(navData, reported, navStartDistRef, navProgressFloorRef),
              99
            )
          );
          if (navData.state) setRobotState(navData.state.toUpperCase());
        }
        return;
      }

      if (navData.state === "reached") {
        // A stray/late "reached" event can arrive after the user has already
        // backed out of the trip (Change Destination/Return to Base/Back to
        // Selection all clear `destinations`) — with no active destination,
        // showing the "complete" screen would render "To: —". Ignore it then.
        if (destinationsRef.current[currentStopIndexRef.current] === undefined) return;
        hasCompletedRef.current = true;
        setNavProgress(100);
        setNavigationTarget(null);
        phaseRef.current = "complete";
        setPhase("complete");
        handleSpeak("Destination reached.");
        return;
      }

      if (phaseRef.current === "stopped" && navData.state === "moving") {
        // Emergency Resume: re-baseline the distance measurement. The floor
        // keeps the bar where it was paused rather than snapping to 0.
        navStartDistRef.current = null;
        phaseRef.current = "navigating";
        setRobotState("MOVING");
        setPhase("navigating");
      }

      if (phaseRef.current !== "navigating") return;

      if (navData.state === "idle") return;

      let progressPct = normalizeProgressPct(navData.progress);
      progressPct = withDistanceProgress(
        navData,
        progressPct,
        navStartDistRef,
        navProgressFloorRef
      );
      progressPct = Math.min(progressPct, 99);

      setNavProgress(progressPct);
      if (navData.state) setRobotState(navData.state.toUpperCase());
      setCurrentStatus(statusForProgress(progressPct));

      if (navData.state === "stopped") {
        if (emergencyStopActive.current) return;
        setBoxedInFailure(false);
        setStoppedProgress(progressPct);
        phaseRef.current = "stopped";
        setPhase("stopped");
        handleSpeak("Navigation paused.");
        return;
      }
      // 'failed' must route to the stopped phase (Resume / Return to Base / Choose
      // Location); falling through to progress left the bar spinning forever.
      // 'error' is the robot's own final tick (e.g. a boxed-in give-up), which
      // reaches us just before the relayed 'failed'.
      if (NAV_FAILURE_STATES.has(navData.state)) {
        setBoxedInFailure(isBoxedInFailure(navData));
        setStoppedProgress(progressPct);
        phaseRef.current = "stopped";
        setPhase("stopped");
        handleSpeak(
          isBoxedInFailure(navData)
            ? "The robot could not get through and needs manual help."
            : "Navigation failed. The robot could not reach the destination."
        );
        return;
      }
      if (navData.state === "cancelled") {
        const returnPoint = navData.return_point?.x !== undefined ? navData.return_point : null;
        setCancelReturnPoint(returnPoint);
        if (returnPoint) {
          // Routed through sendNavigateCommandRef so this goal carries a
          // request_id; an inline fetch would be invisible to de-duplication.
          sendNavigateCommandRef.current?.({
            x: returnPoint.x,
            y: returnPoint.y,
            yaw: returnPoint.theta ?? 0,
          });
          phaseRef.current = "returningToBase";
          setPhase("returningToBase");
          handleSpeak("Navigation cancelled. Robot heading back.");
        } else {
          phaseRef.current = "cancelled";
          setPhase("cancelled");
          handleSpeak("Navigation cancelled.");
        }
      }
    };

    socket.on("robot-status", (msg) => {
      if (msg.robotId !== robotId) return;
      handleNavData(msg.data);
    });

    socket.on("nav_progress", (data) => {
      // Must be filtered by serialID. Unfiltered, another robot's
      // 'failed'/'cancelled'/'reached' drove THIS delivery's phase machine.
      if (data?.serialID != null && robotId && String(data.serialID) !== String(robotId)) return;
      handleNavData(data);
    });

    socket.on("disconnect", () => {
      setRobotSocketId(null);
      setSerialID(null);
    });

    socket.on("BATTERY_STATUS_UPDATE", (msg) => {
      const sid = msg?.robotId ?? msg?.data?.serialID;
      if (sid != null && robotId && String(sid) !== String(robotId)) return;
      const detail = msg?.data?.battery_detail ?? msg?.battery_detail ?? null;
      if (detail) {
        // Derive level from battery_detail.percentage (0–1 range) or fall back to batteryLevel field
        const raw = msg?.data?.batteryLevel;
        let level = null;
        if (typeof raw === "number") {
          level = raw;
        } else if (typeof detail.percentage === "number") {
          level = Math.round(detail.percentage * 100);
        }
        if (typeof level === "number") setBatteryLevel(level);
      } else {
        const level = msg?.data?.batteryLevel;
        if (typeof level === "number") setBatteryLevel(level);
      }
    });

    // SENSOR_STATUS_UPDATE also carries a battery fraction (0-1); merged the
    // same way above, so the reading holds whichever event arrived last.
    socket.on("SENSOR_STATUS_UPDATE", (msg) => {
      if (msg?.serialID != null && robotId && String(msg.serialID) !== String(robotId)) return;
      if (typeof msg?.battery === "number") setBatteryLevel(Math.round(msg.battery * 100));
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotId]);

  // ── Stop management ────────────────────────────────────────────────────
  // The journey strip composes the route by appending/re-pointing/removing
  // stops, so every mutator is functional — several can land in one tick.
  /** Adds a new blank row (its own Select then handles the location choice), same as admin's Add Stop. */
  const appendStop = () => {
    setStops((prev) => (prev.length < MAX_STOPS ? [...prev, makeStop()] : prev));
  };

  // Removing the last stop leaves one blank slot rather than an empty array, so
  // "Start Navigation" stays correctly disabled instead of submitting nothing.
  const removeStop = (index) => {
    setStops((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : blankStops();
    });
  };

  const updateStop = (index, value) => {
    setStops((prev) => {
      if (value && (prev[index - 1]?.name === value || prev[index + 1]?.name === value)) {
        setCommandBlockedMessage(
          "This destination is the same as the adjacent stop. Please choose a different location."
        );
        return prev;
      }
      const next = [...prev];
      next[index] = { ...next[index], name: value };
      return next;
    });
  };

  // Keep the ref aligned with the state it mirrors.
  useEffect(() => {
    isRobotConnectedRef.current = isRobotConnected;
  }, [isRobotConnected]);

  /** Refuse a command to a disconnected or charging robot. Not applied to
   *  cancel/return-to-base — the escape hatches out of a dropped trip. */
  const [commandBlockedMessage, setCommandBlockedMessage] = useState(null);
  // Read through a ref so the gate sees the current value without re-creating
  // every command handler when the charging flag flips.
  const isRobotChargingRef = useRef(batteryCharging);
  useEffect(() => {
    isRobotChargingRef.current = batteryCharging;
  }, [batteryCharging]);
  const canSendCommand = useCallback((action = "send this command", opts = {}) => {
    // `allowWhileCharging` is for commands that aren't navigation (the door), so
    // a docked robot can still be loaded/unloaded. Nav commands are refused.
    if (isRobotChargingRef.current && !opts.allowWhileCharging) {
      setCommandBlockedMessage(
        `Robot is charging — cannot ${action}. Wait until charging is complete.`
      );
      return false;
    }
    if (isRobotConnectedRef.current) return true;
    setCommandBlockedMessage(`Robot is disconnected — cannot ${action}. Wait for it to reconnect.`);
    return false;
  }, []);

  /** Wipe the composed route back to a single empty slot. */
  const clearStops = () => setStops(blankStops());

  // ── API commands ───────────────────────────────────────────────────────
  const sendNavigateCommand = useCallback(
    async (dest) => {
      const token = localStorage.getItem("portalToken");
      // One id per navigation intent: the robot de-duplicates by request_id, so
      // a retry reuses it while a new action gets a fresh one. crypto over
      // Math.random for Sonar S2245, not for security.
      const requestId =
        globalThis.crypto?.randomUUID?.() ??
        `ufe-${Date.now()}-${Array.from(globalThis.crypto.getRandomValues(new Uint32Array(2)))
          .map((n) => n.toString(16))
          .join("")}`;

      try {
        const r = await apiFetch(`${CONFIG.BASE_URL}/api/navigate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            socketId: robotSocketId,
            request_id: requestId,
            data: {
              x: dest.x,
              y: dest.y,
              yaw: dest.yaw ?? dest.theta ?? 0,
              serialID: robotId || serialID,
              request_id: requestId,
            },
            serialID: robotId || serialID,
          }),
        });

        // Must check the parsed body: a bare `r.ok` treats a 404/409 rejection
        // the same as success, showing a navigating screen for a goal that reached nobody.
        const body = await r.json().catch(() => null);
        if (!r.ok || body?.success === false) {
          console.error("Navigate rejected:", r.status, body);
          return { ok: false, status: r.status, body };
        }
        return { ok: true, status: r.status, body };
      } catch (err) {
        console.error("Navigate error:", err);
        return { ok: false, status: 0, body: null };
      }
    },
    [robotSocketId, robotId, serialID]
  );

  // Expose the latest sendNavigateCommand to the socket handlers declared above it.
  useEffect(() => {
    sendNavigateCommandRef.current = sendNavigateCommand;
  }, [sendNavigateCommand]);

  const handleDoorPress = () => {
    const action = isDoorOpen ? "close" : "open";
    if (!canSendCommand(`${action} the door`, { allowWhileCharging: true })) return;
    fetch(`${CONFIG.BASE_URL}/api/${action}-door`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ socketId: robotSocketId, data: { serialID: robotId || serialID } }),
    })
      .then((r) => r.json())
      .catch((err) => console.error("Door error:", err));
    if (isDoorOpen) {
      setIsDoorOpen(false);
      handleSpeak("The door has been closed.");
    } else {
      setIsDoorOpen(true);
      handleSpeak("The door is open. You can place the order inside.");
    }
  };

  const sendReturnToBaseCommand = useCallback(
    (overrideHome = null) => {
      const base = overrideHome || homeLocation;
      const token = localStorage.getItem("portalToken");
      apiFetch(`${CONFIG.BASE_URL}/api/back-to-base`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          data: {
            serialID: robotId || serialID,
            ...(base && {
              x: base.x,
              y: base.y,
              yaw: base.yaw ?? base.theta ?? 0,
              name: base.name,
            }),
          },
        }),
      })
        .then((r) => r.json())
        .catch((err) => console.error("Back to base error:", err));
    },
    [robotId, serialID, homeLocation]
  );

  // ── Derived: base / dock for this map ──────────────────────────────────
  // The operator-pinned return point for this map, else its first location.
  // Labels the journey strip's Base nodes and is the home target on submit.
  const baseLocation = useMemo(() => {
    if (!selectedMap?._id) return null;
    try {
      const saved = localStorage.getItem(`returnToBase_${selectedMap._id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { x: parsed.x, y: parsed.y, yaw: parsed.yaw ?? 0, name: parsed.name };
      }
    } catch {
      /* ignore */
    }
    const first = mapLocations[0];
    return first
      ? { x: first.coords.x, y: first.coords.y, yaw: first.coords.yaw, name: first.name }
      : null;
  }, [selectedMap, mapLocations]);

  // ── Phase transition handlers ──────────────────────────────────────────
  const handleSubmit = () => {
    // Unfilled rows (e.g. a trailing blank left over from Add Stop) are
    // discarded rather than blocking dispatch — only filled stops are sent.
    const filledStops = stops.filter((s) => s.name && s.name !== "");
    if (!selectedMap || filledStops.length === 0) {
      alert("Please select a map and at least one destination.");
      return;
    }
    const hasConsecutiveDuplicate = filledStops.some(
      (s, i) => i > 0 && s.name === filledStops[i - 1].name
    );
    if (hasConsecutiveDuplicate) {
      setCommandBlockedMessage(
        "Two consecutive destinations can't be the same location. Please choose different stops."
      );
      return;
    }
    // Hard block: checked here as well as in SelectionPhase's disabled button,
    // so the guard holds even if this handler is reached another way.
    if (batteryCharging) {
      alert(
        "This robot is currently charging and cannot be dispatched. Please try again once charging is complete."
      );
      return;
    }
    if (!canSendCommand("start navigation")) return;
    // `id` rides along from the stop row so the journey strip has a stable key
    // — a route can legitimately visit the same location name twice.
    const destList = filledStops.map(({ id, name }) => {
      const loc = mapLocations.find((l) => l.name === name);
      return {
        id,
        x: loc.coords.x,
        y: loc.coords.y,
        yaw: loc.coords.yaw,
        theta: loc.coords.yaw,
        name,
      };
    });
    setHomeLocation(baseLocation);
    setDestinations(destList);
    setCurrentStopIndex(0);
    setNavProgress(0);
    resetNavProgressLeg();
    setRobotState("IDLE");
    setCurrentStatus(STATUS_MESSAGES[0]);
    hasCompletedRef.current = false;
    setNavigationTarget(destList[0]);

    // Only show the navigating UI once the backend confirms dispatch — otherwise
    // a rejected command (e.g. 409 ALREADY_NAVIGATING) shows a robot that never moved.
    sendNavigateCommand(destList[0]).then((result) => {
      if (!result.ok) {
        setNavigationTarget(null);
        phaseRef.current = "selection";
        setPhase("selection");
        alert(navigateFailureMessage(result));
        return;
      }
      phaseRef.current = "navigating";
      setPhase("navigating");
      handleSpeak(
        destList.length === 1
          ? "Destination set. Starting navigation."
          : `${destList.length} stops set. Starting navigation.`
      );
    });
  };

  const handleStopNavigation = useCallback(() => {
    const token = localStorage.getItem("portalToken");
    apiFetch(`${CONFIG.BASE_URL}/api/navigate-cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ socketId: robotSocketId, data: { serialID: robotId || serialID } }),
    })
      .then((r) => r.json())
      .then(() => {
        sendReturnToBaseCommand();
      })
      .catch((err) => console.error("Stop nav error:", err));
    phaseRef.current = "returningToBase";
    setNavProgress(0);
    resetNavProgressLeg();
    setNavigationTarget(null);
    setPhase("returningToBase");
    handleSpeak("Navigation cancelled. Robot is heading back.");
  }, [robotSocketId, robotId, serialID, sendReturnToBaseCommand]);

  // "Stop Here": cancel the nav goal only — no back-to-base goal follows, so the
  // robot halts wherever it currently is instead of auto-driving home.
  const handleStopInPlace = useCallback(() => {
    const token = localStorage.getItem("portalToken");
    apiFetch(`${CONFIG.BASE_URL}/api/navigate-cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ socketId: robotSocketId, data: { serialID: robotId || serialID } }),
    })
      .then((r) => r.json())
      .catch((err) => console.error("Stop nav error:", err));
    setCancelReturnPoint(null);
    phaseRef.current = "cancelled";
    setNavProgress(0);
    resetNavProgressLeg();
    setNavigationTarget(null);
    setPhase("cancelled");
    handleSpeak("Navigation cancelled. Robot has stopped.");
  }, [robotSocketId, robotId, serialID]);

  // An e-stop only latches the motors; the robot still tracks `dest`. Resume
  // must only release the latch — a fresh goal to a robot that already has it
  // active broke resume.
  const handleResume = async () => {
    const dest = destinations[currentStopIndex];
    if (!dest) return;
    if (!canSendCommand("resume navigation")) return;
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/adbToSocket/emergency-resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ serialID: robotId || serialID }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || body?.success === false) {
        alert("Resume failed. The robot did not respond.");
        return;
      }
    } catch (err) {
      console.error("Resume (emergency-resume) error:", err);
      alert("Resume failed. The robot did not respond.");
      return;
    }
    hasCompletedRef.current = false;
    setNavigationTarget(dest);
    setNavProgress(stoppedProgress);
    // Seed the floor from the paused value rather than 0, avoiding a visible
    // drop when the leg resumes.
    resetNavProgressLeg(stoppedProgress);
    phaseRef.current = "navigating";
    setRobotState("MOVING");
    setPhase("navigating");
    handleSpeak("Resuming navigation.");
  };

  const handleNextStop = () => {
    const nextIdx = currentStopIndex + 1;
    if (nextIdx >= destinations.length) {
      handleBackToSelection();
      return;
    }
    if (!canSendCommand("navigate to the next stop")) return;

    const nextDest = destinations[nextIdx];
    hasCompletedRef.current = false;
    resetNavProgressLeg();
    setNavigationTarget(nextDest);

    // Advance the stop index only once the goal is accepted, so a refused
    // next-stop does not silently skip a delivery.
    sendNavigateCommand(nextDest).then((result) => {
      if (!result.ok) {
        setNavigationTarget(destinations[currentStopIndex] ?? null);
        alert(navigateFailureMessage(result));
        return;
      }
      phaseRef.current = "navigating";
      setCurrentStopIndex(nextIdx);
      setNavProgress(0);
      setPhase("navigating");
      handleSpeak(`Navigating to stop ${getStopLabel(nextIdx)}: ${nextDest.name}`);
    });
  };

  // After a boxed-in give-up the backend drops the goal, so Resume (which only
  // lifts an e-stop latch) would leave the robot standing. Re-send the leg.
  const handleRetryLeg = () => {
    const dest = destinations[currentStopIndex];
    if (!dest) return;
    if (!canSendCommand("send the robot again")) return;
    hasCompletedRef.current = false;
    resetNavProgressLeg(stoppedProgress);
    setNavigationTarget(dest);
    sendNavigateCommand(dest).then((result) => {
      if (!result.ok) {
        alert(navigateFailureMessage(result));
        return;
      }
      phaseRef.current = "navigating";
      setNavProgress(stoppedProgress);
      setRobotState("MOVING");
      setPhase("navigating");
      handleSpeak(`Trying again: ${dest.name}`);
    });
  };

  const handleReturnToBase = useCallback(() => {
    hasCompletedRef.current = false;
    phaseRef.current = "returningToBase";
    sendReturnToBaseCommand();
    setNavProgress(0);
    resetNavProgressLeg();
    setRobotState("MOVING");
    setIsDoorOpen(false);
    setNavigationTarget(null);
    setDestinations([]);
    setCurrentStopIndex(0);
    setPhase("returningToBase");
    handleSpeak("Returning to base.");
  }, [sendReturnToBaseCommand]);

  useEffect(() => {
    const onEStop = () => {
      emergencyStopActive.current = true;
    };
    const onEResume = () => {
      emergencyStopActive.current = false;
    };
    window.addEventListener("delivery:emergencyStop", onEStop);
    window.addEventListener("delivery:emergencyResume", onEResume);
    return () => {
      window.removeEventListener("delivery:emergencyStop", onEStop);
      window.removeEventListener("delivery:emergencyResume", onEResume);
    };
  }, []);

  useEffect(() => {
    const handler = () => handleReturnToBase();
    window.addEventListener("fab:returnToBase", handler);
    return () => window.removeEventListener("fab:returnToBase", handler);
  }, [handleReturnToBase]);

  useEffect(() => {
    const handler = () => {
      if (phaseRef.current === "navigating") handleStopNavigation();
    };
    window.addEventListener("fab:emergencyStop", handler);
    return () => window.removeEventListener("fab:emergencyStop", handler);
  }, [handleStopNavigation]);

  const handleBackToSelection = () => {
    setNavigationTarget(null);
    setPhase("selection");
    setStops(blankStops());
    setNavProgress(0);
    resetNavProgressLeg();
    setDestinations([]);
    setCurrentStopIndex(0);
    setIsDoorOpen(false);
    hasCompletedRef.current = false;
  };

  /** Cancel navigation and return to selection; blocked while moving — see canSendCommand. */
  const handleChangeDestination = useCallback(() => {
    if (robotState === "MOVING") {
      setCommandBlockedMessage(
        "Robot is moving — cannot change destination. Stop the robot first."
      );
      return;
    }
    const token = localStorage.getItem("portalToken");
    apiFetch(`${CONFIG.BASE_URL}/api/navigate-cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ socketId: robotSocketId, data: { serialID: robotId || serialID } }),
    }).catch((err) => console.error("Change dest cancel error:", err));
    hasCompletedRef.current = false;
    phaseRef.current = "selection";
    setNavigationTarget(null);
    setPhase("selection");
    setStops(blankStops());
    setNavProgress(0);
    resetNavProgressLeg();
    setDestinations([]);
    setCurrentStopIndex(0);
  }, [robotSocketId, robotId, serialID, robotState]);

  const closeMapSelectionDialogModal = () => setShowMapSelectionModalDialog(false);

  // ── Derived values ─────────────────────────────────────────────────────
  const currentDest = destinations[currentStopIndex];
  const hasNextStop = currentStopIndex < destinations.length - 1;
  const roverTypeRaw = robot?.roverType;
  const roverTypeStr = Array.isArray(roverTypeRaw) ? (roverTypeRaw[0] ?? "") : (roverTypeRaw ?? "");
  const isAeroRobot = typeof roverTypeStr === "string" && roverTypeStr.toLowerCase() === "aero";

  const previewLocations = useMemo(() => {
    if (phase !== "selection") return [];
    return stops
      .filter((s) => s.name && s.name !== "")
      .map(({ name }) => {
        const loc = mapLocations.find((l) => l.name === name);
        return loc ? { x: loc.coords.x, y: loc.coords.y, name } : null;
      })
      .filter(Boolean);
  }, [stops, mapLocations, phase]);

  const handleEmergencyResume = useCallback(() => {
    const dest = destinations[currentStopIndex];
    if (!dest) return;
    // Resuming after an e-stop re-issues a navigation goal, so it takes the same
    // charging/connectivity gate as every other nav command.
    if (!canSendCommand("resume navigation")) return;
    // Re-issuing the goal after an e-stop can be refused like any other goal, so
    // report it instead of leaving the operator to wonder why nothing moved.
    sendNavigateCommand(dest).then((result) => {
      if (!result.ok) alert(navigateFailureMessage(result));
    });
  }, [destinations, currentStopIndex, sendNavigateCommand, canSendCommand]);

  const layoutProps = {
    selectedMap,
    navigationTarget,
    previewLocations,
    robot,
    // Breadcrumb only when reached from Robot Details ▸ Operations; the
    // sidebar/My Robots route has no single robot's page to point back to.
    fromRobotId: presetRobotId,
    batteryLevel,
    batteryCharging,
    showMapSelectionModalDialog,
    closeMapSelectionDialogModal,
    isDoorOpen,
    onDoorPress: handleDoorPress,
    isAeroRobot,
    isRobotConnected,
    portalRobots,
    onRobotChange: handleRobotChange,
    onEmergencyResume: handleEmergencyResume,
  };

  // ── Phase rendering ────────────────────────────────────────────────────
  // One builder per phase, looked up rather than chained: the phases are
  // mutually exclusive, so an if/else ladder only added branching to read.
  const homeDest = homeLocation
    ? { id: "home", ...homeLocation, name: homeLocation.name || "Base" }
    : null;
  // The homeward leg is a one-element route, so these are constants here rather
  // than three more ternaries inside the switch below.
  const homeDestList = homeDest ? [homeDest] : [];
  const homeStopIndex = homeDest ? 1 : 0;

  // A switch rather than an if/else ladder: the phases are mutually exclusive,
  // and a switch costs the same to read no matter how many there are.
  let pageContent = null;
  switch (phase) {
    case "selection":
      pageContent = (
        <SelectionPhase
          {...layoutProps}
          mapsAndIds={mapsAndIds}
          // The map comes from the robot's assignment; manual picking is offered
          // only when it genuinely has none, or the assigned one was deleted.
          onMapChange={
            needsManualMap
              ? (id) => setSelectedMap(mapsAndIds?.find((m) => m._id === id) || null)
              : undefined
          }
          needsManualMap={needsManualMap}
          stops={stops}
          mapLocations={mapLocations}
          onAppendStop={appendStop}
          onRemoveStop={removeStop}
          onUpdateStop={updateStop}
          onClearStops={clearStops}
          onSubmit={handleSubmit}
        />
      );
      break;
    case "navigating":
      pageContent = (
        <NavigatingPhase
          {...layoutProps}
          boxedIn={boxedIn}
          robotState={robotState}
          currentStatus={currentStatus}
          navProgress={navProgress}
          destinations={destinations}
          currentStopIndex={currentStopIndex}
          currentDest={currentDest}
          onStopNavigation={handleStopNavigation}
          onStopInPlace={handleStopInPlace}
          onReturnToBase={handleReturnToBase}
          onChangeDestination={handleChangeDestination}
        />
      );
      break;
    case "stopped":
      pageContent = (
        <StoppedPhase
          {...layoutProps}
          boxedIn={boxedIn}
          boxedInFailure={boxedInFailure}
          onRetry={handleRetryLeg}
          stoppedProgress={stoppedProgress}
          currentDest={currentDest}
          destinations={destinations}
          currentStopIndex={currentStopIndex}
          onResume={handleResume}
          onReturnToBase={handleReturnToBase}
        />
      );
      break;
    case "cancelled":
      pageContent = (
        <CancelledPhase
          {...layoutProps}
          cancelReturnPoint={cancelReturnPoint}
          onBackToSelection={handleBackToSelection}
        />
      );
      break;
    case "complete":
      pageContent = (
        <CompletePhase
          {...layoutProps}
          currentDest={currentDest}
          destinations={destinations}
          currentStopIndex={currentStopIndex}
          hasNextStop={hasNextStop}
          navMode={navMode}
          onNextStop={handleNextStop}
          onReturnToBase={handleReturnToBase}
          onChangeDestination={handleBackToSelection}
          onRate={handleRate}
        />
      );
      break;
    // Reuses NavigatingPhase: a single-element `destinations` resolves the
    // existing fromLabel/subtitle to "Base", and `returningToBase` hides
    // Return to Base/Change Destination.
    case "returningToBase":
      pageContent = (
        <NavigatingPhase
          {...layoutProps}
          boxedIn={boxedIn}
          robotState={robotState}
          currentStatus="Almost home."
          navProgress={navProgress}
          destinations={homeDestList}
          currentStopIndex={homeStopIndex}
          currentDest={homeDest}
          returningToBase
          onStopNavigation={handleStopNavigation}
          onReturnToBase={handleReturnToBase}
          onChangeDestination={handleChangeDestination}
        />
      );
      break;
    default:
      break;
  }

  return (
    <>
      {pageContent}
      <Snackbar
        open={commandBlockedMessage != null}
        autoHideDuration={3000}
        onClose={() => setCommandBlockedMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setCommandBlockedMessage(null)} severity="error" variant="filled">
          {commandBlockedMessage || ""}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RoomNumberPageAero;
